import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { useI18n } from 'builder/hooks/useI18n'
import { Job, actions } from 'builder/modules/jobTracking'
import { TooltipV2 } from 'builder/components/Tooltip'
import { ButtonSize, ButtonTheme, ButtonV2 } from 'builder/components/Button'

import { documentSectionId } from '../Sidebar'
import { SkipAutoTailoringModal } from './SkipAutoTailoringModal'
import { useSkip } from './hooks/useSkip'
import { useRetry } from './hooks/useRetry'
import { ActionContainer } from './styles'

const TRANSLATION = 'builder.job_tracking.auto_apply.auto_tailoring'

type Props = {
  job: Job
}

export const AutoTailoringActions = ({ job }: Props) => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { mutateAsync: skipAutoTailoring, isLoading: skipIsLoading } = useSkip({ id: job.id })
  const { mutateAsync: retryAutoTailoring, isLoading: retryIsLoading } = useRetry({ id: job.id })

  const [confirmModal, setConfirmModal] = useState(false)

  const onConfirmModal = async () => {
    await skipAutoTailoring()
    setConfirmModal(false)
    dispatch(actions.updateJob({ ...job, autoApplyStatus: 'processing' }))
  }

  const onRetry = async () => {
    await retryAutoTailoring()
    navigate(`#${documentSectionId}`)
    dispatch(
      actions.updateJob({
        ...job,
        autoTailoringStatus: 'processing',
        autoApplyStatus: 'processing',
      }),
    )
  }

  return (
    <>
      <ActionContainer>
        <TooltipV2
          multiline
          isActive={job.autoTailoringRetryCount >= 3}
          value={i18n.t(`${TRANSLATION}.limit_tooltip`)}
        >
          <div>
            <ButtonV2
              onClick={onRetry}
              size={ButtonSize.small}
              isLoading={retryIsLoading}
              isDisabled={job.autoTailoringRetryCount >= 3}
              theme={ButtonTheme.default}
              style={{ width: '100%' }}
            >
              {i18n.t(`${TRANSLATION}.try_again`)}
            </ButtonV2>
          </div>
        </TooltipV2>
        <ButtonV2
          size={ButtonSize.small}
          theme={ButtonTheme.secondary}
          onClick={() => setConfirmModal(true)}
        >
          {i18n.t(`${TRANSLATION}.without_tailoring`)}
        </ButtonV2>
      </ActionContainer>
      {confirmModal && (
        <SkipAutoTailoringModal
          isLoading={skipIsLoading}
          onConfirmModal={onConfirmModal}
          onCloseModal={() => setConfirmModal(false)}
        />
      )}
    </>
  )
}
